import React, { forwardRef, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import FormInput from './FormInput';
import SelectInput from './SelectInput';
import SearchableSelectInput from './SearchableSelectInput';
import CraInternalContactSelect from './CraInternalContactSelect';
import { adminAPI } from '../api';
import DollarIcon from '../../../../assets/images/reskin-images/icon--dollar.svg';
import Toggle from './Toggle';
import useAuth from '../context/AuthContext';
import useAlarm from '../context/AlarmContext';

const UserInviteForm = ({
  handleChange,
  courses,
  formData,
  alexAccessTypes,
  hubspotAccessTypes,
  cohorts,
  limitFields,
  includedFields,
  toggleIncludedField,
  application,
  isEditUser = false,
  isMultiEditUser = false,
  inviteFormActive,
  setInviteFormActive,
  onCraContactChange,
}) => {
  const { user_invite: userInvite } = formData;
  const { userData } = useAuth();
  const { setAlarm } = useAlarm();
  const roles = userData?.roles || [];
  const hasInvite = userInvite?.id;

  const [localInviteFormExists, setLocalInviteFormExists] = useState(
    isEditUser ? !!hasInvite : true
  );
  const inviteFormExists =
    isEditUser && setInviteFormActive != null
      ? inviteFormActive
      : localInviteFormExists;
  const setInviteFormExists =
    isEditUser && setInviteFormActive != null
      ? setInviteFormActive
      : setLocalInviteFormExists;
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [isCheckAll, setIsCheckAll] = useState(
    selectedCourses.length === courses.length
  );
  const queryClient = useQueryClient();

  const mutation = useMutation(adminAPI.destroyInvites, {
    onSuccess: () => {
      queryClient.invalidateQueries('activeUsers');
      setInviteFormExists(false);
      setAlarm({ type: 'success', message: 'Invite deleted successfully' });
    },
    onError: (error) => {
      setAlarm({
        type: 'error',
        message: 'Failed to delete invite: ' + error.message,
      });
      setContent();
    },
  });

  const handleDeleteInvite = () => {
    if (hasInvite) {
      const serializedData = JSON.stringify([{ id: userInvite.id }]);
      mutation.mutate(serializedData);
    } else {
      setAlarm({ type: 'error', message: 'No invite to delete' });
    }
  };

  useEffect(() => {
    const initiallyEnrolledCourses = courses
      .filter((course) => course.enrolled_in)
      .map((course) => course?.id);
    setSelectedCourses(initiallyEnrolledCourses);
  }, [courses]);

  const handleCheckAll = (checked) => {
    if (checked) {
      setSelectedCourses(courses.map((course) => course?.id));
      setIsCheckAll(true);
    } else {
      setSelectedCourses([]);
      setIsCheckAll(false);
    }
  };

  const handleCourseCheck = (checked, courseId) => {
    if (checked) {
      setSelectedCourses((prevCourses) => [...prevCourses, courseId]);
    } else {
      setSelectedCourses((prevCourses) =>
        prevCourses.filter((id) => id !== courseId)
      );
    }
  };

  useEffect(() => {
    if (!inviteFormExists) return;
    handleChange('course_ids', selectedCourses, 'user_invite');
    setIsCheckAll(selectedCourses.length === courses.length);
  }, [selectedCourses]);

  useEffect(() => {
    if (inviteFormExists) {
      if (showField('course_ids') || !limitFields) {
        handleChange('course_ids', selectedCourses, 'user_invite');
      }
      if (showField('invitation_access') || !limitFields) {
        handleChange(
          'invitation_access',
          userInvite?.invitation_access || 'limited',
          'user_invite'
        );
      }
    }
  }, [inviteFormExists, selectedCourses, includedFields]);

  const today = new Date();
  const oneWeekFromToday = new Date(today);
  oneWeekFromToday.setDate(today.getDate() + 7);
  const oneYearFromToday = new Date(today);
  oneYearFromToday.setFullYear(today.getFullYear() + 1);
  const [invitationAccess, setInvitationAccess] = useState(
    userInvite?.invitation_access || 'limited'
  );
  const [selectedDate, setSelectedDate] = useState(
    userInvite?.expires_at
      ? new Date(userInvite?.expires_at)
      : invitationAccess === 'limited'
      ? oneWeekFromToday
      : oneYearFromToday
  );
  const [skipEmail, setSkipEmail] = useState(isEditUser || isMultiEditUser|| !!userInvite?.skip_email);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!inviteFormExists) {
      handleChange('user_invite', null);
    }
  }, [inviteFormExists]);

  useEffect(() => {
    if (!inviteFormExists) return;
    handleChange('skip_email', skipEmail, 'user_invite');
  }, [skipEmail]);

  useEffect(() => {
    if (!inviteFormExists) return;
    if (!showField('expiration_type') && limitFields) return;

    if (!userInvite?.expiration_type) {
      if (!hasInvite || hasChanges) {
        if (showField('expires_at') || !limitFields) {
          setSelectedDate(oneWeekFromToday);
          handleChange('expires_at', oneWeekFromToday, 'user_invite');
        }
      }
      if (showField('expiration_type') || !limitFields) {
        handleChange('expiration_type', 'date', 'user_invite');
      }
    } else {
      if (userInvite?.expiration_type === 'length') {
        if (!hasInvite || hasChanges) {
          if (showField('expires_at') || !limitFields) {
            setSelectedDate(oneWeekFromToday);
            handleChange('expires_at', oneWeekFromToday, 'user_invite');
          }
        }
        if (showField('valid_for_days') || !limitFields) {
          handleChange('valid_for_days', 7, 'user_invite');
        }
        setToggleLengthField(true);
      }
      if (userInvite?.expiration_type === 'date') {
        if (showField('valid_for_days') || !limitFields) {
          handleChange('valid_for_days', null, 'user_invite');
        }
        setToggleLengthField(false);
      }
    }
  }, [userInvite?.expiration_type, includedFields, inviteFormExists]);

  useEffect(() => {
    if (!inviteFormExists) return;
    if (!showField('invitation_access') && limitFields) return;
    
    if (!hasInvite || hasChanges) {
      if (invitationAccess === 'limited') {
        if (showField('expires_at') || !limitFields) {
          setSelectedDate(oneWeekFromToday);
          handleChange('expires_at', oneWeekFromToday, 'user_invite');
        }
      }
      if (invitationAccess === 'unlimited') {
        if (showField('expires_at') || !limitFields) {
          setSelectedDate(oneYearFromToday);
          handleChange('expires_at', oneYearFromToday, 'user_invite');
        }
      }
    }
  }, [invitationAccess, includedFields, inviteFormExists]);

  const handleInvitationAccessChange = (value) => {
    setInvitationAccess(value);
    handleChange('invitation_access', value, 'user_invite');
  };

  const showField = (fieldId) => {
    if (!limitFields) return true;
    return includedFields?.includes(fieldId);
  };

  const [validForDays, setValidForDays] = useState(userInvite?.valid_for_days);
  const [toggleLengthField, setToggleLengthField] = useState(!isEditUser);
  const [lengthType, setLengthType] = useState(userInvite?.length_type);

  useEffect(() => {
    if (!inviteFormExists) return;
    if (!showField('expires_at') && limitFields) return;

    if (!toggleLengthField && selectedDate && !userInvite?.expires_at) {
      handleChange('expires_at', selectedDate, 'user_invite');
    }
  }, [selectedDate, toggleLengthField, userInvite?.expires_at, includedFields, inviteFormExists]);

  const [showRatePricing, setShowRatePricing] = useState(
    !!userInvite?.discount_cents > 0
  );

  const [selectedCohort, setSelectedCohort] = useState(
    userInvite?.cohorts || ''
  );

  const [alexAccessType, setAlexAccessType] = useState(
    alexAccessTypes.some(
      (option) => option.value === userInvite?.alex_access_type
    )
      ? userInvite?.alex_access_type
      : 'N/A'
  );

  const [accessType, setAccessType] = useState(
    hubspotAccessTypes.some(
      (option) => option.value === userInvite?.access_type
    )
      ? userInvite?.access_type
      : 'N/A'
  );

  useEffect(() => {
    if (!showField('alex_expiration_type')) return;

    if (
      userInvite?.is_alex_available &&
      (!userInvite?.alex_expiration_type ||
        userInvite?.alex_expiration_type === 'fixed_date') &&
      !userInvite?.alex_expiration_date &&
      selectedDate
    ) {
      handleChange('alex_expiration_date', selectedDate, 'user_invite');
    }
  }, [
    selectedDate,
    userInvite?.alex_expiration_date,
    userInvite?.alex_expiration_type,
    userInvite?.is_alex_available,
    includedFields
  ]);

  useEffect(() => {
    if (!inviteFormExists) return;
    if (!showField('alex_expiration_type')) return;

    if (userInvite?.expiration_type === 'length') {
      handleChange('valid_for_days', 7, 'user_invite');
      handleChange('length_type', 'days', 'user_invite');
      handleChange('expires_at', null, 'user_invite');
      setToggleLengthField(true);
    }
    if (userInvite?.expiration_type === 'date') {
      handleChange('valid_for_days', null, 'user_invite');
      setToggleLengthField(false);
    }
  }, [userInvite?.expiration_type, includedFields, inviteFormExists]);

  useEffect(() => {
    setValidForDays(userInvite?.valid_for_days);
  }, [userInvite?.valid_for_days]);

  useEffect(() => {
    setLengthType(userInvite?.length_type);
  }, [userInvite?.length_type]);

  return inviteFormExists || isMultiEditUser ? (
    <div style={{ gap: '2rem' }} className="flex flex-col mt-4">

      {!isEditUser && (
        <p className="text-sm uppercase text-charcoal font-bold">
          User Details
        </p>
      )}

      { !isEditUser && cohorts && (
      <div>
        <div className="flex items-center">
          <p className="text-sm uppercase text-charcoal font-bold">
            Cohort
          </p>
          {limitFields && (
            <Toggle
              triggerFunction={toggleIncludedField}
              field="cohorts"
              className="ml-4"
            />
          )}
        </div>
        {showField('cohorts') && (
          <SearchableSelectInput
            className="mt-4"
            name="cohorts"
            value={selectedCohort}
            options={[
              { value: '', label: 'Select cohort' },
              ...cohorts
            ]}
            onChange={(name, value) => {
              setSelectedCohort(value);
              handleChange(name, value, 'user_invite');
            }}
            placeholder="Search cohorts..."
          />
        )}
      </div>
      )}

      {(isEditUser || !limitFields || isMultiEditUser) && (
        <div>
          <div className="flex items-center">
            <p className="text-sm uppercase text-charcoal font-bold">
              CRA Internal Contact
            </p>
            {limitFields && (
              <Toggle
                triggerFunction={toggleIncludedField}
                field="invited_by_id"
                className="ml-4"
              />
            )}
          </div>
          {(!limitFields || isMultiEditUser || showField('invited_by_id')) && (
            <CraInternalContactSelect
              hideLabel
              className="mt-2"
              value={userInvite?.invited_by_id}
              selectedEmail={userInvite?.invited_by_email}
              onInteraction={onCraContactChange}
              onChange={(id, email) => {
                onCraContactChange?.();
                handleChange('invited_by_id', id, 'user_invite');
                handleChange('invited_by_email', email, 'user_invite');
              }}
            />
          )}
        </div>
      )}

      {application?.includes('ALD') && (
        <>
          {/* Modules */}
          <div>
            { !isEditUser && (
              <p className="text-sm uppercase text-charcoal mb-8 font-bold">
                User Invite Details
              </p>
            )}
            <div className="mt-2 flex items-center">
              <p className="text-sm uppercase text-charcoal font-bold">
                Modules
              </p>
              {limitFields && (
                <Toggle
                  triggerFunction={toggleIncludedField}
                  field="course_ids"
                  className="ml-4"
                />
              )}
            </div>
            {showField('course_ids') && (
              <div className="rounded-lg border-2 border-gray p-2 mt-4 relative overflow-hidden">
                <div className="p-4 flex items-center border-b-2 border-gray">
                  <input
                    id="select-all"
                    className="mr-4"
                    type="checkbox"
                    checked={isCheckAll}
                    onChange={(e) => handleCheckAll(e.target.checked)}
                  />
                  <label htmlFor="select-all">Select All</label>
                </div>
                {courses.map((course) => {
                  const { id, title } = course;
                  return (
                    <div className="px-4 py-2 flex items-center" key={id}>
                      <input
                        id={id}
                        className="mr-4"
                        type="checkbox"
                        checked={selectedCourses.includes(id)}
                        onChange={(e) =>
                          handleCourseCheck(e.target.checked, id)
                        }
                      />
                      <label htmlFor={id}>{title}</label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Access Type */}
      {application?.includes('ALD') && (
      <div>
        <div className="flex items-center">
          <p className="text-sm uppercase text-charcoal  font-bold">
            Access Types
          </p>
          {limitFields && (
            <Toggle
              triggerFunction={toggleIncludedField}
              field="access_type"
              className="ml-4"
            />
          )}
        </div>
        {hubspotAccessTypes && showField('access_type') && (
          <SelectInput
            className="mt-4"
            name="access_type"
            value={accessType}
            options={hubspotAccessTypes}
            onChange={(name, value) => {
              setAccessType(value);
              handleChange(name, value, 'user_invite');
            }}
          />
        )}
      </div>
      )}

      {/* Alex Access Type */}
      {application?.toUpperCase().includes('ALEX') && (
      <div>
        <div className="flex items-center">
          <p className="text-sm uppercase text-charcoal  font-bold">
            ALEX Access Types
          </p>
          {limitFields && (
            <Toggle
              triggerFunction={toggleIncludedField}
              field="alex_access_type"
              className="ml-4"
            />
          )}
        </div>
        {alexAccessTypes && showField('alex_access_type') && (
          <SelectInput
            className="mt-4"
            name="alex_access_type"
            value={alexAccessType}
            options={alexAccessTypes}
            onChange={(name, value) => {
              setAlexAccessType(value);
              handleChange(name, value, 'user_invite');
            }}
          />
        )}
      </div>
      )}
      {/* Add invitation Access */}
      {!isEditUser && (
        <div className="">
          <div className="flex items-center">
            <p className="text-sm uppercase text-charcoal  font-bold">
              {userInvite?.id
                ? 'Update Invitation Access'
                : 'Add Invitation Access'}
            </p>
            {limitFields && (
              <Toggle
                triggerFunction={toggleIncludedField}
                field="invitation_access"
                className="ml-4"
              />
            )}
          </div>

          {showField('invitation_access') && (
            <div className="flex rounded-lg overflow-hidden border-2 border-gray mt-4">
              <button
                onClick={() => {
                  handleInvitationAccessChange('limited');
                  setHasChanges(true);
                }}
                type="button"
                className={`flex flex-col items-center justify-center py-4 w-1/2 ${
                  invitationAccess === 'limited' && 'bg-purple-200'
                }`}
              >
                <p className="text-sm  font-semibold">Limited</p>
                <p className="text-sm  mt-2">Defaults to one week.</p>
                <p className="text-sm ">Sends "Week Access" email</p>
              </button>
              <button
                onClick={() => {
                  handleInvitationAccessChange('unlimited');
                  setHasChanges(true);
                }}
                type="button"
                className={`flex flex-col items-center justify-center py-4 w-1/2 ${
                  invitationAccess === 'unlimited' && 'bg-purple-200'
                }`}
              >
                <p className="text-sm  font-semibold">Unlimited</p>
                <p className="text-sm  mt-2">Defaults to one year.</p>
                <p className="text-sm ">Sends "Welcome" email</p>
              </button>
            </div>
          )}
        </div>
      )}
      {/* Add expiration type */}
      {!isEditUser && (
        <div className="">
          <div className="flex items-center">
            <p className="text-sm uppercase text-charcoal  font-bold">
              {userInvite?.id ? 'Update Expiration Type' : 'Add Expiration Type'}
            </p>
            {limitFields && (
              <Toggle
                triggerFunction={toggleIncludedField}
                field="expiration_type"
                className="ml-4"
              />
            )}
          </div>
          {showField('expiration_type') && (
            <div className="flex rounded-lg overflow-hidden border-2 border-gray mt-4">
              <button
                onClick={() => {
                  handleChange('expiration_type', 'length', 'user_invite');
                }}
                type="button"
                className={`flex flex-col items-center justify-center py-4 w-1/2 ${
                  userInvite?.expiration_type === 'length' && 'bg-purple-200'
                }`}
              >
                <p className="text-sm  font-semibold">Fixed Length</p>
                <p className="text-sm  mt-2">
                  User has access for a fixed period
                </p>
                <p className="text-sm  mt-2">after joining.</p>
              </button>
              <button
                onClick={() => {
                  handleChange('expires_at', selectedDate, 'user_invite');
                  handleChange('expiration_type', 'date', 'user_invite');
                }}
                type="button"
                className={`flex flex-col items-center justify-center py-4 w-1/2 ${
                  userInvite?.expiration_type === 'date' && 'bg-purple-200'
                }`}
              >
                <p className="text-sm  font-semibold">Fixed Date </p>
                <p className="text-sm  mt-2">
                  User has access until selected date.
                </p>
              </button>
            </div>
          )}
        </div>
      )}
      {/* Invitation Length */}
      {toggleLengthField && !isEditUser && (
        <div className="">
          <div className="flex items-center">
            <p className="text-sm uppercase text-charcoal  font-bold">
              Invitation Length
            </p>
            {limitFields && (
              <Toggle
                triggerFunction={toggleIncludedField}
                field="valid_for_days"
                className="ml-4"
              />
            )}
          </div>
          {showField('valid_for_days') && (
            <FormInput
              handleChange={(e) => {
                const val = e.target.value;
                handleChange('valid_for_days', val === '' ? '' : Number(val), 'user_invite');
              }}
              onBlur={(e) => {
                if (e.target.value === '') {
                  handleChange('valid_for_days', 7, 'user_invite');
                }
              }}
              type="number"
              value={userInvite?.valid_for_days ?? ''}
              style={{ maxWidth: '80px' }}
            />
          )}

          {/* Length Type (Days/Years) - show whenever Invitation Length is included so bulk edit can set it */}
          {showField('valid_for_days') && (
            <div className="flex rounded-lg overflow-hidden border-2 border-gray mt-4">
              <button
                onClick={() => {
                  handleChange('length_type', 'days', 'user_invite');
                }}
                type="button"
                className={`flex flex-col items-center justify-center py-4 w-1/2 ${
                  userInvite?.length_type === 'days' && 'bg-purple-200'
                }`}
              >
                <p className="text-sm  font-semibold">Days</p>
              </button>
              <button
                onClick={() => {
                  handleChange('length_type', 'years', 'user_invite');
                }}
                type="button"
                className={`flex flex-col items-center justify-center py-4 w-1/2 ${
                  userInvite?.length_type === 'years' && 'bg-purple-200'
                }`}
              >
                <p className="text-sm  font-semibold">Years</p>
              </button>
            </div>
          )}
        </div>
      )}
      {(!toggleLengthField || isEditUser) && (
        <div>
          <div className="flex items-center">
            <p className="text-sm uppercase text-charcoal  font-bold">
              Expiration Date
            </p>
            {limitFields && (
              <Toggle
                triggerFunction={toggleIncludedField}
                field="expires_at"
                className="ml-4"
              />
            )}
          </div>
          {selectedDate && showField('expires_at') && (
            <FormInput
              value={
                userInvite?.expires_at
                  ? new Date(userInvite.expires_at).toISOString().split('T')[0]
                  : selectedDate.toISOString().split('T')[0]
              }
              type="date"
              handleChange={(e) => {
                const newDate = e.target.value ? new Date(e.target.value) : null;
                setSelectedDate(newDate);
                handleChange('expires_at', newDate, 'user_invite');
                handleChange('alex_expiration_date', newDate, 'user_invite');
              }}
            />
          )}
        </div>
      )}


      {!isEditUser && (
        <div>
          <div className="flex items-center">

            <p className="text-sm uppercase text-charcoal font-bold">
              Is Alex Available?
            </p>
            {limitFields && (
              <Toggle
              triggerFunction={toggleIncludedField}
              field="is_alex_available"
              className="ml-4"
              />
            )}
          </div>
          {showField('is_alex_available') && (
            <div className="flex items-center mt-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_alex_available"
                  value="true"
                  defaultChecked
                  onChange={(e) => handleChange('is_alex_available', true)}
                  className="mr-2"
                />
                <span className="text-sm">YES</span>
              </label>
              <label className="flex items-center ml-6">
                <input
                  type="radio"
                  name="is_alex_available"
                  value="false"
                  onChange={(e) => handleChange('is_alex_available', false)}
                  className="mr-2"
                />
                <span className="text-sm">NO</span>
              </label>
            </div>
          )}

          {userInvite?.is_alex_available === true && (
            <>
              <div className="mt-8">
                <div className="flex items-center">
                  <p className="text-sm uppercase text-charcoal  font-bold">
                    {userInvite?.id ? 'Update ALEX Expiration Type' : 'Add ALEX Expiration Type'}
                  </p>
                  {limitFields && (
                    <Toggle
                    triggerFunction={(toggle, field) => {
                      toggleIncludedField(toggle, field);
                      toggleIncludedField(toggle, 'alex_expiration_date');
                      toggleIncludedField(toggle, 'alex_append_days');
                      toggleIncludedField(toggle, 'alex_length_type');
                    }}
                    field="alex_expiration_type"
                    className="ml-4"
                    />
                  )}
                </div>
                {showField('alex_expiration_type') && (
                  <div className="flex rounded-lg overflow-hidden border-2 border-gray mt-4">
                    <button
                      onClick={() => {
                        handleChange('alex_expiration_date', selectedDate, 'user_invite');
                        handleChange('alex_expiration_type', 'fixed_date', 'user_invite');
                      }}
                      type="button"
                      className={`flex flex-col items-center justify-center py-4 w-1/2 ${
                        userInvite?.alex_expiration_type === 'fixed_date' && 'bg-purple-200'
                      }`}
                    >
                      <p className="text-sm  font-semibold">Fixed Date</p>
                      <p className="text-sm  mt-2">
                        User has ALEX expiring in a fixed date
                      </p>
                    </button>
                    <button
                      onClick={() => {
                        handleChange('alex_expiration_type', 'append_length', 'user_invite');
                      }}
                      type="button"
                      className={`flex flex-col items-center justify-center py-4 w-1/2 ${
                        userInvite?.alex_expiration_type === 'append_length' && 'bg-purple-200'
                      }`}
                    >
                      <p className="text-sm  font-semibold">Append Length</p>
                      <p className="text-sm  mt-2">
                        User has ALEX expiration appending with the length after joining or after the end of the user's current subscription
                      </p>
                    </button>
                  </div>
                )}
              </div>

              {userInvite?.alex_expiration_type === 'append_length' && showField('alex_expiration_type') && (
                <div className="mt-4">
                  <div className="flex items-center">
                    <p className="text-sm uppercase text-charcoal  font-bold">
                      Append Length
                    </p>
                  </div>
                  <FormInput
                    handleChange={(e) => {
                      const val = e.target.value;
                      handleChange('alex_append_days', val === '' ? '' : Number(val), 'user_invite');
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        handleChange('alex_append_days', 7, 'user_invite');
                      }
                    }}
                    type="number"
                    value={userInvite?.alex_append_days ?? ''}
                    style={{ maxWidth: '80px' }}
                  />

                  {/* Length Type */}

                  <div className="flex rounded-lg overflow-hidden border-2 border-gray mt-4">
                    <button
                      onClick={() => {
                        handleChange('alex_length_type', 'days', 'user_invite');
                      }}
                      type="button"
                      className={`flex flex-col items-center justify-center py-4 w-1/2 ${
                        userInvite?.alex_length_type === 'days' && 'bg-purple-200'
                      }`}
                    >
                      <p className="text-sm  font-semibold">Days</p>
                    </button>
                    <button
                      onClick={() => {
                        handleChange('alex_length_type', 'years', 'user_invite');
                      }}
                      type="button"
                      className={`flex flex-col items-center justify-center py-4 w-1/2 ${
                        userInvite?.alex_length_type === 'years' && 'bg-purple-200'
                      }`}
                    >
                      <p className="text-sm  font-semibold">Years</p>
                    </button>
                  </div>
                </div>
              )}
              {userInvite?.alex_expiration_type === 'fixed_date' && showField('alex_expiration_type')&& (
                <div className="mt-4">
                  <div className="flex items-center">
                    <p className="text-sm uppercase text-charcoal  font-bold">
                      ALEX Expiration Date
                    </p>
                  </div>
                  <FormInput
                    value={
                      userInvite.alex_expiration_date
                        ? new Date(userInvite.alex_expiration_date)
                            .toISOString()
                            .split('T')[0]
                        : selectedDate
                        ? selectedDate.toISOString().split('T')[0]
                        : ''
                    }
                    type="date"
                    handleChange={(e) => {
                      handleChange('alex_expiration_date', e.target.value, 'user_invite');
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Misc */}
      <div className="">
        <p className="text-sm uppercase text-charcoal  font-bold mb-4">Misc.</p>
        <div className="rounded-lg border-2 border-gray p-2 mt-4">
          {application?.includes('ALD') && (
            <div className="flex items-center">
              <input
                className="mx-4"
                type="checkbox"
                checked={userInvite?.unlimited_gifts ?? false}
                onChange={(e) =>
                  handleChange(
                    'unlimited_gifts',
                    e.target.checked,
                    'user_invite'
                  )
                }
                disabled={!showField('unlimited_gifts')}
              />
              <p className={!showField('unlimited_gifts') ? 'gray-text' : undefined}>
                Allow unlimiting gifting
              </p>
              {limitFields && (
                <Toggle
                  triggerFunction={toggleIncludedField}
                  field="unlimited_gifts"
                  className="ml-4"
                />
              )}
            </div>
          )}
          <div className="flex">
            <input
              className="mx-4"
              type="checkbox"
              checked={skipEmail}
              onChange={(e) => {
                setSkipEmail(e.target.checked);
              }}
            />
            <p>Skip sending email</p>
          </div>
          {application?.includes('ALD') && (
            <>
              <div className="flex">
                <input
                  className="mx-4"
                  type="checkbox"
                  checked={userInvite?.opt_out_eop ?? false}
                  onChange={(e) =>
                    handleChange('opt_out_eop', e.target.checked, 'user_invite')
                  }
                />
                <p>Opt out of EOP</p>
              </div>
              {showField('discount_cents') && (
                <div className="flex">
                  <input
                    className="mx-4"
                    type="checkbox"
                    checked={showRatePricing}
                    onChange={() => {
                      if (!showRatePricing === false) {
                        handleChange('discount_cents', 0, 'user_invite');
                      }
                      setShowRatePricing(!showRatePricing);
                    }}
                  />
                  <p>Set preferred rate pricing</p>
                </div>
              )}
              {showRatePricing && (
                <div className="flex items-center">
                  <FormInput
                    handleChange={(e) =>
                      handleChange(
                        'discount_cents',
                        e.target.value,
                        'user_invite'
                      )
                    }
                    type="number"
                    value={userInvite.discount_cents}
                    icon={DollarIcon}
                  />
                </div>
              )}
            </>
          )}
          {application?.includes('ALEX') && !isEditUser && (
            <>
              <div className="flex">
                <input
                  className="mx-4"
                  type="checkbox"
                  checked={userInvite?.use_allegis_holiday_email_template ?? false}
                  onChange={(e) => handleChange('use_allegis_holiday_email_template', e.target.checked, 'user_invite')}
                />
                <p>Use Allegis Holiday Email Template</p>
              </div>
            </>
          )}
        </div>
      </div>
      {(roles?.includes('admin') || roles?.includes('manager')) &&
        isEditUser && (
          <button
            type="button"
            className="bg-purple-100 hover:bg-purple-200 p-4 flex items-center justify-center rounded-lg mt-2 font-semibold "
            onClick={handleDeleteInvite}
          >
            Delete Invite
          </button>
        )}
    </div>
  ) : (
    <div className="flex items-center flex-col w-full">
      <p>User does not have an invite</p>
      <button
        type="button"
        className="bg-purple-100 hover:bg-purple-200 p-4 flex items-center justify-center rounded-lg mt-2 font-semibold "
        onClick={() => setInviteFormExists(true)}
      >
        Create Invite
      </button>
    </div>
  );
};

export default UserInviteForm;
